import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import { RootState } from '../RootState';
import axios from "axios";
import { uiBus } from '@/plugins/uiBus';
import {settings} from "@/plugins/settings";
import {cookies} from "@/plugins/cookies";
import responseHandler from "@/plugins/errorHandling";
import {IErrors} from "@/model/IErrors";
import {CookieAttributes} from "js-cookie";

var jwtDecode = require('jwt-decode');

const namespaced: boolean = true;
const KEY_TOKEN = 'authToken';
const IMAGE_TOKEN: string = 'imageToken';
const KEY_USER_DATA = 'authUserData';

const IMAGE_TOKEN_COOKIE_OPTIONS: CookieAttributes = {
  secure: true,
  expires: 1,
  domain: settings.imageCookieDomain,
  path: '/api/m1/image',
};

const LONG_EXP_AUTH_COOKIE_OPTIONS: CookieAttributes = {
  secure: cookies.secure,
  expires: 42,
  domain: cookies.domain,
  path: cookies.path,
};

export interface IAuthUserPermission {
  permission: number;
  target_id: number;
}
export interface IAuthUserData {
  id?: number;
  email?: string;
  role?: string;
  permissions?: IAuthUserPermission[];
}

export interface IActionAccessLevel {
  action: string;
  level: number;
}

interface AuthState {
  token?: string;
  error: boolean;
  userData: IAuthUserData;
  actionAccessLevels: IActionAccessLevel[];
  errors: IErrors;
}

const token = cookies.get(KEY_TOKEN);
const userData = JSON.parse(localStorage.getItem(KEY_USER_DATA) || '{}');

const state: AuthState = {
  token,
  error: false,
  userData,
  actionAccessLevels: [],
  errors: {},
};

axios.defaults.headers.common['Authorization'] = 'bearer ' + state.token;

const getters: GetterTree<AuthState, RootState> = {
  token: (state): string|undefined => state.token,
  error: (state): boolean => state.error,
  userData: (state): IAuthUserData => state.userData,
  actionAccessLevels: (state): IActionAccessLevel[] => state.actionAccessLevels,
  canAction(state): (action: string) => boolean {
    return function(action: string) {
      const accessLevel = state.actionAccessLevels.find(each => each.action == action);
      return !!accessLevel && !!accessLevel.level;
    };
  },
};

const actions: ActionTree<AuthState, RootState> = {
  login({ commit }, {email, password}): any {
    commit('loginRequest');
    return axios({
      url: settings.endpoint + 'login',
      method: 'POST',
      data: { email, password, },
    })
    .then((response: any) => {
      commit('loginOk', response.data.data);
    })
    .catch((error: any) => {
      commit('loginError');
      throw error;
    });
  },

  logout({ commit }): any {
    commit('logout');
    return axios({
      url: settings.endpoint + 'logout',
      method: 'POST',
    })
    .then((response: any) => {
      commit('logoutOk');
    })
    .catch((error: any) => {
      commit('logoutError');
      throw error;
    });
  },
  // we receive this data in login, but we keep refreshing it
  fetchActionAccessLevels({commit}): any {
    return responseHandler.debounce<IActionAccessLevel[]>(
      state.actionAccessLevels,
      300,
      () => axios({
        url: settings.endpoint + 'me/actionAccessLevels'
      }),
      commit,
      'actionAccessLevelsLoaded'
    );
  },
};

const mutations: MutationTree<AuthState> = {
  loginRequest(state) {
    state.token = undefined;
    state.error = false;
  },
  loginOk(state, payload) {
    const token = payload.token;
    const data: any = jwtDecode(token);
    const userData = {
      id: data.sub,
      email: data.uem,
      role: data.uro,
      permissions: data.upe,
    };
    state.token = token;
    state.error = false;
    state.userData = userData;
    state.actionAccessLevels = payload.actionAccessLevels;

    if (userData.id == 57392) {
      cookies.set(KEY_TOKEN, token, LONG_EXP_AUTH_COOKIE_OPTIONS);
    }
    else {
      cookies.set(KEY_TOKEN, token);
    }
    cookies.set(IMAGE_TOKEN, token, IMAGE_TOKEN_COOKIE_OPTIONS);

    localStorage.setItem(KEY_USER_DATA, JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = 'bearer ' + token;
    uiBus.emit('login.OK');
  },
  loginError(state) {
    state.error = true;
    uiBus.emit('login.KO');
  },
  logout(state) {
    state.token = undefined;
    state.error = false;
    state.userData = {};
    state.actionAccessLevels = [];
    cookies.remove(KEY_TOKEN);
    localStorage.removeItem(KEY_USER_DATA);
    uiBus.emit('login.KO');
  },
  actionAccessLevelsLoaded(state, payload) {
    state.actionAccessLevels = payload;
  },
  errors(state, payload) {
    state.errors = payload;
  },
};

export const authModule: Module<AuthState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
